import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type QuoteRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { AcceptQuoteSheet } from './AcceptQuoteSheet';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';

/**
 * The replies, side by side.
 *
 * ── THIS IS AN OFFER LADDER, AND IT IS DELIBERATELY THE SAME IDEA ───────────
 *
 * A product page shows one medicine and eighteen pharmacies competing on it.
 * This shows one prescription and however many pharmacies will fill it. Same
 * promise, same ordering — cheapest first — and the same reason: a buyer of
 * medicine in Nigeria is comparing prices whether or not we help them.
 *
 * ── WHAT A BUYER MUST NOTICE BEFORE THEY ACCEPT ─────────────────────────────
 *
 * A substitution. A pharmacy that cannot get the brand on the paper will offer
 * a generic, which is usually right and occasionally not what the prescriber
 * meant. It is flagged on the line, not buried in the pharmacist's note, and it
 * is the one thing on this screen with its own colour.
 *
 * ── AND WHAT ACCEPTING DOES ─────────────────────────────────────────────────
 *
 * Creates an ordinary order and sends them to the ordinary checkout. There is
 * no separate prescription invoice, because there is no separate payment path.
 */
export function QuoteComparisonScreen() {
  const { reference = '' } = useParams<{ reference: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['quote-request', reference],
    queryFn: ({ signal }) => api.quoteRequest(reference, signal),
    // Replies arrive while the buyer is looking at this screen.
    refetchInterval: 30_000,
  });

  // Which quote the buyer is in the middle of accepting. Expanding a card
  // shows what is in it; this is them saying "this one, now where and how".
  const [accepting, setAccepting] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: (input: { quoteReference: string; addressId?: number; method: string }) =>
      api.acceptQuote(reference, input.quoteReference, input.method as never, input.addressId),
    onSuccess: (order) => {
      void queryClient.invalidateQueries({ queryKey: ['quote-requests'] });
      navigate(`/orders/${order.reference}`);
    },
  });

  if (isError) {
    return (
      <>
        <AppBar back title="Your request" />
        <ErrorState
          title="We couldn't load this request"
          body="Your connection dropped, or this request is no longer on your account."
          action={
            <Button full icon="refresh" onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      </>
    );
  }

  if (isLoading || !data) {
    return (
      <>
        <AppBar back title="Your request" />
        <div className="mx-auto max-w-[720px] space-y-3 px-4 py-5">
          <Skeleton className="h-20 w-full" radius="card" />
          <Skeleton className="h-40 w-full" radius="card" />
        </div>
      </>
    );
  }

  // Live replies, plus the one they accepted. A buyer looking back at a
  // finished request needs to see WHAT they agreed to — hiding it the moment
  // it stops being an offer is how somebody ends up unable to check whether
  // the substitution they approved is the thing that arrived.
  const live = data.quotes.filter((quote) => quote.is_live || quote.status === 'accepted');
  const acceptError = accept.error instanceof ApiError ? accept.error : null;

  return (
    <>
      <AppBar back title={data.reference} />

      <div className="mx-auto w-full max-w-[720px] px-4 pb-8 pt-4 xl:px-6">
        <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
          {data.reference}
        </p>
        <h1 className="m-0 mt-1 font-display text-[21px] font-bold leading-tight tracking-[-0.02em]">
          {data.status_label}
        </h1>

        <p className="m-0 mt-1.5 text-[13px] leading-relaxed text-muted">
          {data.wanted.map((item) => [item.name, item.quantity].filter(Boolean).join(' × ')).join(', ') ||
            data.note}
        </p>

        {data.is_veterinary && data.species_label ? (
          <p className="m-0 mt-1.5 text-[12.5px] text-blue">
            For {data.species_label.toLowerCase()}
            {data.animal_note ? ` · ${data.animal_note}` : ''}
          </p>
        ) : null}

        {data.order_reference ? (
          <Card className="mt-4 border-verified bg-verified-tint">
            <p className="m-0 flex items-center gap-2 text-[14px] font-bold text-verified-ink">
              <Icon name="check" size={17} />
              Accepted
            </p>
            <p className="m-0 mt-1 text-[13px] leading-relaxed text-ink">
              This is now order {data.order_reference}. Everything from here on works exactly like
              any other ChiCare order.
            </p>
            <Button className="mt-3" onClick={() => navigate(`/orders/${data.order_reference}`)}>
              Open the order
            </Button>
          </Card>
        ) : null}

        {acceptError ? (
          <p role="alert" className="m-0 mt-4 rounded-card border border-danger p-3 text-[13px] font-medium leading-relaxed text-danger">
            {acceptError.message}
          </p>
        ) : null}

        {/* Who was asked, who replied, and who opened the prescription. §9.15
            is not only a storage rule — it is something the buyer can see. */}
        <WhereItWent request={data} />

        {live.length === 0 && !data.order_reference ? (
          <Card className="mt-4">
            <p className="m-0 text-[13.5px] font-bold text-ink">No replies yet</p>
            <p className="m-0 mt-1 text-[13px] leading-relaxed text-muted">
              Most come within a few hours. We will tell you as each one arrives — you do not need
              to keep this open.
            </p>
          </Card>
        ) : null}

        <div className="mt-4 space-y-2.5">
          {live.map((quote, index) => (
            <QuoteCard
              key={quote.reference}
              quote={quote}
              cheapest={index === 0 && live.length > 1}
              open={expanded === quote.reference}
              onToggle={() => setExpanded(expanded === quote.reference ? null : quote.reference)}
              accepting={accepting === quote.reference}
              onStartAccepting={() => setAccepting(quote.reference)}
              onCancelAccepting={() => setAccepting(null)}
              onAccept={(input) => accept.mutate({ quoteReference: quote.reference, ...input })}
              busy={accept.isPending}
              acceptError={acceptError}
              requestReference={data.reference}
              decided={data.order_reference !== null}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function QuoteCard({
  quote,
  cheapest,
  open,
  onToggle,
  accepting,
  onStartAccepting,
  onCancelAccepting,
  onAccept,
  busy,
  acceptError,
  requestReference,
  decided,
}: {
  quote: QuoteRow;
  cheapest: boolean;
  open: boolean;
  onToggle: () => void;
  accepting: boolean;
  onStartAccepting: () => void;
  onCancelAccepting: () => void;
  onAccept: (input: { addressId?: number; method: string }) => void;
  busy: boolean;
  acceptError: ApiError | null;
  requestReference: string;
  decided: boolean;
}) {
  const substitutions = quote.lines.filter((line) => line.is_substitute).length;

  return (
    <Card flush className={cn(cheapest && 'border-blue')}>
      <button type="button" onClick={onToggle} className="block w-full p-4 text-left">
        <div className="flex flex-wrap items-center gap-2">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-blue-tint font-display text-[12px] font-bold text-blue">
            {quote.pharmacy_initials}
          </span>
          <b className="min-w-0 flex-1 truncate text-[14px] text-ink">{quote.pharmacy}</b>
          {/* The all-in number, because it is the one they will be charged.
              Goods alone would mean showing a total, taking an acceptance, and
              then adding delivery — which is the one thing this flow must
              never do. The breakdown sits underneath for anybody checking. */}
          <span className="text-right">
            <span className="block font-display text-[19px] font-bold leading-none tracking-[-0.02em] text-blue tabular">
              {quote.all_in_display}
            </span>
            <span className="mt-0.5 block text-[10.5px] text-faint tabular">
              {quote.goods_total_display} + {quote.delivery_display} delivery
            </span>
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {quote.status === 'accepted' ? <Badge tone="verified">You accepted this</Badge> : null}
          {cheapest && quote.status !== 'accepted' ? <Badge tone="brand">Lowest</Badge> : null}
          <Badge tone="neutral">
            {quote.lines.length} {quote.lines.length === 1 ? 'item' : 'items'}
          </Badge>
          {/* The one thing on this screen worth its own colour. */}
          {substitutions > 0 ? (
            <Badge tone="caution">
              {substitutions} substitut{substitutions === 1 ? 'ion' : 'ions'}
            </Badge>
          ) : null}
        </div>

        {quote.note ? (
          <p className="m-0 mt-2 text-[12.5px] leading-relaxed text-muted">{quote.note}</p>
        ) : null}

        <span className="mt-2 flex items-center gap-1 text-[12px] font-bold text-blue">
          {open ? 'Hide what they are sending' : 'See what they are sending'}
          <Icon name={open ? 'up' : 'down'} size={13} strokeWidth={2.4} />
        </span>
      </button>

      {open ? (
        <div className="border-t border-line px-4 py-3">
          <ul className="m-0 list-none space-y-2.5 p-0">
            {quote.lines.map((line, index) => (
              <li key={index} className="flex flex-wrap items-baseline gap-x-2">
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-medium text-ink">
                    {line.name}
                    {line.quantity > 1 ? ` × ${line.quantity}` : null}
                  </span>

                  {line.is_substitute ? (
                    <span className="mt-0.5 block text-[11.5px] leading-snug text-caution-ink">
                      Instead of {line.substitute_for ?? 'what was prescribed'}
                      {line.note ? ` — ${line.note}` : ''}
                    </span>
                  ) : line.note ? (
                    <span className="mt-0.5 block text-[11.5px] leading-snug text-faint">
                      {line.note}
                    </span>
                  ) : null}
                </span>

                <span className="font-mono text-[12.5px] text-ink tabular">
                  {line.line_total_display}
                </span>
              </li>
            ))}
          </ul>

          {!decided && !accepting ? (
            <Button className="mt-4 w-full" onClick={onStartAccepting}>
              Take this one
            </Button>
          ) : null}

          {!accepting ? (
            <p className="m-0 mt-2 text-[11.5px] leading-relaxed text-muted">
              About {quote.delivery_display} of that is delivery from {quote.pharmacy} — the exact
              figure depends on where it is going, which you pick next.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Where it is going, what that costs, and then pay. Never a total shown
          and then a larger one charged. */}
      {accepting ? (
        <AcceptQuoteSheet
          requestReference={requestReference}
          quote={quote}
          busy={busy}
          error={acceptError}
          onAccept={onAccept}
          onCancel={onCancelAccepting}
        />
      ) : null}
    </Card>
  );
}

/**
 * Where the prescription went.
 *
 * Shown to the buyer rather than kept in an audit table, because §9.15 exists
 * for the person whose health data it is, not for the auditor.
 */
function WhereItWent({ request }: { request: { sent_to: Array<{ pharmacy: string | null; status: string; declined_reason: string | null; opened_prescription_at: string | null }>; has_prescription: boolean } }) {
  if (request.sent_to.length === 0) {
    return null;
  }

  return (
    <section className="mt-4 rounded-card border border-line bg-white">
      <p className="m-0 border-b border-line px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
        Who you sent it to
      </p>

      <ul className="m-0 list-none p-0">
        {request.sent_to.map((one, index) => (
          <li
            key={index}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line-2 px-4 py-2.5 last:border-b-0"
          >
            <span className="min-w-0 flex-1 text-[13px] text-ink">{one.pharmacy}</span>

            <span className="text-[11.5px] text-faint">
              {one.status === 'quoted'
                ? 'Replied'
                : one.status === 'declined'
                  ? one.declined_reason || 'Cannot fill it'
                  : 'Not yet'}
            </span>

            {request.has_prescription ? (
              <span className="w-full font-mono text-[10.5px] text-faint">
                {one.opened_prescription_at
                  ? `Opened your prescription ${new Date(one.opened_prescription_at).toLocaleString('en-NG')}`
                  : 'Has not opened your prescription'}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
